/* Pure helpers for the Epic → Story → Task tree the API returns
   (GET /issues/hierarchy/:projectId). Mirrors server/src/services/issueHierarchy. */

const LEVELS = { epic: 0, story: 1 };
export const levelOf = (issueType) => LEVELS[String(issueType || "").trim().toLowerCase()] ?? 2;
export const canParent = (parentType, childType) => levelOf(parentType) < levelOf(childType);

export const childTypesOf = (parentType) => {
  const level = levelOf(parentType);
  if (level === 0) return ["Story", "Task", "Bug"];
  if (level === 1) return ["Task", "Bug"];
  return [];
};

export const isDoneStatus = (status) => String(status || "").trim().toLowerCase() === "done";

export const assigneeOf = (issue) => {
  const person = Array.isArray(issue?.assignee) ? issue.assignee[0] : issue?.assignee;
  if (!person || (!person.employeeName && !person.employeeEmail)) return null;
  return person;
};

/** Every node, depth first. */
export const walk = (nodes = [], visit, parent = null) => {
  nodes.forEach((node) => {
    visit(node, parent);
    walk(node.children || [], visit, node);
  });
};

export const flattenAll = (roots) => {
  const all = [];
  walk(roots, (node) => all.push(node));
  return all;
};

export const idsWithChildren = (roots) => {
  const ids = [];
  walk(roots, (node) => {
    if (node.children?.length) ids.push(String(node._id));
  });
  return ids;
};

/**
 * Keep the nodes that match, plus the ancestors needed to show where they
 * sit. Ancestors that don't match themselves are flagged `isContext` so the
 * table can dim them. Returns a new tree; the input is untouched.
 */
export const filterTree = (roots = [], matches) => {
  const prune = (nodes) =>
    nodes.reduce((kept, node) => {
      const children = prune(node.children || []);
      const self = matches(node);
      if (self || children.length) kept.push({ ...node, children, isContext: !self });
      return kept;
    }, []);
  return prune(roots);
};

/**
 * The rows to draw: each node, then its children when it is expanded.
 * `expanded` is a Set of ids; `expandAll` opens everything (used while
 * filtering, so matches are never hidden inside a closed parent).
 */
export const visibleRows = (roots = [], expanded = new Set(), expandAll = false) => {
  const rows = [];
  const add = (nodes) =>
    nodes.forEach((node) => {
      const hasChildren = Boolean(node.children?.length);
      const open = hasChildren && (expandAll || expanded.has(String(node._id)));
      rows.push({ node, hasChildren, open });
      if (open) add(node.children);
    });
  add(roots);
  return rows;
};

/** Issues that `child` may be moved under: higher level, and not itself. */
export const validParents = (roots, child) =>
  flattenAll(roots).filter(
    (node) => String(node._id) !== String(child?._id) && canParent(node.issueType, child?.issueType)
  );

/** Counts per type for the header ("4 epics · 12 stories · 30 tasks"). */
export const countByLevel = (roots) => {
  const counts = { epics: 0, stories: 0, tasks: 0 };
  walk(roots, (node) => {
    const level = levelOf(node.issueType);
    if (level === 0) counts.epics += 1;
    else if (level === 1) counts.stories += 1;
    else counts.tasks += 1;
  });
  return counts;
};

/** The three status bar segments of a parent, as whole percentages. */
export const rollupSegments = (rollup) => {
  const total = rollup?.total || 0;
  if (!total) return [];
  return [
    { key: "done", label: "Done", count: rollup.done },
    { key: "inProgress", label: "In progress", count: rollup.inProgress },
    { key: "todo", label: "To do", count: rollup.todo },
  ]
    .filter((s) => s.count > 0)
    .map((s) => ({ ...s, percent: (s.count / total) * 100 }));
};
